// import { useNavigate } from "react-router";

import { processStatusEnum, type ProcessType } from "@/types/entities/process";
import { formatToRelativeTime } from "@/utils/dates";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import EllipsisDropdown from "./EllipsisDropdown";
import { getStatusBadgeColor } from "@/utils/process-ui";
import type { ProcessStatus } from "../constants";
import { useMemo } from "react";

export default function ProcessCard({
  process,
  onDuplicate,
  onApproved,
  onSelectEdit,
}: {
  process: ProcessType;
  onDuplicate: (process: ProcessType) => void;
  onApproved: (id: string, currentStatus: ProcessStatus) => void;
  onSelectEdit: (process: ProcessType) => void;
}) {
  const iconColor = useMemo(() => {
    let iconColor = "";
    switch (process.status) {
      case processStatusEnum.active:
        iconColor = "success";
        break;
      case processStatusEnum.inactive:
        iconColor = "neutral";
        break;
      case processStatusEnum.onConstruction:
        iconColor = "action";
        break;
      default:
        iconColor = "neutral";
    }
    return iconColor;
  }, [process.status]);

  // const navigate = useNavigate();

  const userName = process.createdBy?.name
    ? `${process.createdBy?.name ?? ""} ${process.createdBy?.last_name ?? ""}`
    : "Desconocido";

  return (
    <div className="w-full flex items-center gap-4 bg-neutral-object border-[0.5px] border-neutral min-h-[68px] rounded-xl p-3 shadow-neutral-xs cursor-pointer transition-shadow hover:shadow-neutral-md">
      <Avatar shape="square" size="lg">
        <AvatarFallback
          color={iconColor as "neutral" | "success" | "action"}
          type="icon"
          shape="square"
        >
          <Icon type="fas" name="project-diagram" className="text-current" />
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-2">
          {/* Icono o imagen del tag */}
          {process.tags && process.tags.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Avatar size="2xs" shape="square">
                  <AvatarImage src={process.tags[0]?.files?.[0]?.url} />
                  <AvatarFallback
                    color={process.tags[0]?.active ? "neutral" : "warning"}
                    shape="square"
                  >
                    <Icon name="tag" type="fas" />
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                {process.tags[0]?.active ? (
                  <span>{process.tags[0]?.name}</span>
                ) : (
                  <div className="text-center justify-start">
                    <span className="text-[#F59E0B]/90 text-sm font-semibold font-mono leading-6">
                      Inactiva:
                    </span>
                    <span className="text-[#76869E] text-sm font-normal leading-6">
                      {" "}
                      {process.tags[0]?.name}
                    </span>
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          )}
          <p className="font-normal text-base font-geist text-body-loud truncate leading-tight">
            {process.name || ""}
          </p>
        </div>

        <span className="text-body-quiet font-geist text-xs font-normal leading-snug mt-0.5">
          Última actualización {formatToRelativeTime(process?.updated_at || "")}{" "}
          | Creado el{" "}
          {dayjs(process?.created_at || new Date()).format("DD MMMM, YYYY")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Badge size="sm" variant={getStatusBadgeColor(process.status)}>
          {process.status === processStatusEnum.active
            ? "Activo"
            : process.status === processStatusEnum.onConstruction
              ? "En construcción"
              : "Archivado"}
        </Badge>
        <Tooltip>
          <TooltipTrigger asChild>
            <Avatar size="2xs">
              <AvatarFallback color="action">
                {(process.createdBy?.name || "")?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="center">
            <span>{userName}</span>
          </TooltipContent>
        </Tooltip>
        <EllipsisDropdown
          entity="process"
          id={process.id}
          onDuplicate={() => {
            setTimeout(() => {
              onDuplicate(process);
            }, 100);
          }}
          onSelect={() => {
            setTimeout(() => {
              onSelectEdit(process);
            }, 100);
          }}
          onApproved={onApproved}
          status={process.status}
          canApprove={process.can_be_publish}
        />
      </div>
    </div>
  );
}
